/**
 * shared context for the bulk payment "create from template" flow.
 *
 * - fetches and formats template data from the `?templateId` URL param
 * - converts template payment data into form-ready BulkPaymentRowType[]
 * - tracks which payment rows the user has toggled off (excludedRows)
 * - determines field editability based on what the template provides
 * - guards against re-populating the form on screen remounts (hasPopulatedForm)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { modules } from "byzantine";
import {
  useGetTemplate,
  formatBulkTemplate,
} from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import type { FormattedBulkTemplate } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/types";
import type { BulkPaymentRowType } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import AccountContext from "../../../contexts/AccountContext";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";
import { useSudoContext } from "../../../SudoContext";

const EMPTY_RECIPIENT_MAP: Record<string, API.Recipient> = {};

type TemplateContextType = {
  isTemplateMode: boolean;
  template: FormattedBulkTemplate | null;
  loading: boolean;
  templateFields: {
    fromAccount: boolean;
    secCode: boolean;
    entryDesc: boolean;
    description: boolean;
  };
  excludedRows: Set<number>;
  toggleRowInclusion: (index: number) => void;
  isPaymentFieldEditable: (
    rowIndex: number,
    field: "amount" | "description" | "recipient" | "transactionType" | "date"
  ) => boolean;
  allowExpedite: boolean;
  maxExpediteChoice: number;
  convertedPayments: BulkPaymentRowType[];
  templateQueryString: string;
  hasPopulatedForm: boolean;
  markFormPopulated: () => void;
  resetTemplateState: () => void;
};

const defaultContext: TemplateContextType = {
  isTemplateMode: false,
  template: null,
  loading: false,
  templateFields: {
    fromAccount: false,
    secCode: false,
    entryDesc: false,
    description: false,
  },
  excludedRows: new Set(),
  toggleRowInclusion: () => {},
  isPaymentFieldEditable: () => true,
  allowExpedite: false,
  maxExpediteChoice: 0,
  convertedPayments: [],
  templateQueryString: "",
  hasPopulatedForm: false,
  markFormPopulated: () => {},
  resetTemplateState: () => {},
};

const TemplateContext = createContext<TemplateContextType>(defaultContext);

export const useTemplateContext = () => useContext(TemplateContext);

export const TemplateContextProvider = ({
  children,
  scheduledDate,
}: {
  children: ReactNode;
  // scheduledDate is computed in routes.tsx from cutoff/business day logic.
  // templates don't store dates, so we assign this to every converted payment
  scheduledDate: string;
}) => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("templateId");
  const { pathname } = useLocation();
  // the sudo step-up renders outside this flow's URL space
  const { requestOtpRoute, submitOtpRoute } = useSudoContext();
  const isOnSudoRoute =
    pathname === requestOtpRoute || pathname === submitOtpRoute;
  const { getTemplate, loading: templateLoading } = useGetTemplate();
  const [template, setTemplate] = useState<FormattedBulkTemplate | null>(null);
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [hasPopulatedForm, setHasPopulatedForm] = useState(false);
  const { accounts } = useContext(AccountContext);
  const { ach_templates: achTemplatesFeature } = useUserFeatures();

  const recipients = modules.achPayments.usePermissionedRecipients();

  // source of truth for template mode — derived from URL and feature flag
  const isTemplateMode = Boolean(achTemplatesFeature && templateId);
  // appended to all navigate() calls so the templateId persists across screens.
  const templateQueryString = isTemplateMode ? `?templateId=${templateId}` : "";

  const resetTemplateState = useCallback(() => {
    setTemplate(null);
    setExcludedRows(new Set());
    setHasPopulatedForm(false);
  }, []);

  // when templateId changes (user selected a different template), reset all state
  // so the new template gets fetched and the form gets re-populated
  // however, establishSudo navigates to /payment-activity/sudo/request-otp with no
  // query string — templateId is briefly null. resetting on that transient
  // null would wipe the user's row exclusions mid-verification so add a guard
  const lastTemplateIdRef = useRef<string | null>(templateId);
  useEffect(() => {
    if (isOnSudoRoute) return;
    if (lastTemplateIdRef.current === templateId) return;
    lastTemplateIdRef.current = templateId;
    resetTemplateState();
  }, [templateId, isOnSudoRoute, resetTemplateState]);

  useEffect(() => {
    if (!isTemplateMode || !templateId || template) return;

    getTemplate(templateId as API.BulkTemplateId, {
      onData: (data: API.BulkTemplate) => {
        const formatted = formatBulkTemplate(
          data,
          accounts as unknown as API.AnyAccount[]
        );
        setTemplate(formatted);
      },
    });
  }, [templateId, getTemplate, accounts, template]);

  // lookup table for recipients by ID, used for template payment rows
  // short-circuits when there is no template
  const recipientMap = useMemo(() => {
    if (!template) return EMPTY_RECIPIENT_MAP;
    return recipients.reduce<Record<string, API.Recipient>>((map, r) => {
      map[r.id] = r;
      return map;
    }, {});
  }, [recipients, template]);

  // determine which field is hidden from PaymentDetailsSection and shown
  // as read-only in TemplateDetailsCard
  const templateFields = {
    fromAccount: Boolean(template?.institutionAccount),
    secCode: Boolean(template?.secCode),
    entryDesc: Boolean(template?.entryDesc),
    description: Boolean(template?.description),
  };

  // payments sorted by their template-defined order, used as the base
  // for both templatePaymentsMeta and convertedPayments.
  const sortedPaymentData = useMemo(() => {
    if (!template) return [];
    return [...template.paymentData].sort((a, b) => a.order - b.order);
  }, [template]);

  // template payment data converted to BulkPaymentRowType[] for the form.
  const convertedPayments = useMemo<BulkPaymentRowType[]>(() => {
    if (sortedPaymentData.length === 0) return [];
    // wait until recipients are loaded so recipientMap lookups don't resolve to undefined
    const allResolved = sortedPaymentData.every(
      (p) => recipientMap[p.recipient]
    );
    if (!allResolved) return [];

    return sortedPaymentData.map((payment) => ({
      recipient: recipientMap[payment.recipient],
      description: payment.description,
      amount: payment.amount,
      transactionType:
        payment.transaction_type === "credit"
          ? ("send" as const)
          : ("collect" as const),
      scheduledDate,
      // speedStatus means "what this row is eligible for"
      speedStatus: "",
      speedStatusWithExpediteOption: "",
      templateRowId: payment.id,
    }));
  }, [sortedPaymentData, recipientMap, scheduledDate]);

  // the ceiling a template places on each payment's speed
  const maxExpediteChoice = template?.maxExpediteChoice ?? 0;
  // when we save a template and enable expedite, the template's max_expedite_choice is set to 2
  const allowExpedite = maxExpediteChoice === 2;

  // toggle a payment row's inclusion/exclusion
  // excluded rows are filtered out before submission
  const toggleRowInclusion = useCallback((index: number) => {
    setExcludedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // in template mode, fields provided by the template are read-only
  const isPaymentFieldEditable = useCallback(
    (
      rowIndex: number,
      field: "amount" | "description" | "recipient" | "transactionType" | "date"
    ) => {
      if (!isTemplateMode) return true;
      if (field === "date") return true;

      const payment = sortedPaymentData[rowIndex];
      if (!payment) return true;

      switch (field) {
        case "amount":
          return payment.amount <= 0;
        case "description":
          return !payment.description;
        case "recipient":
        case "transactionType":
          return false;
        default:
          return true;
      }
    },
    [isTemplateMode, sortedPaymentData]
  );

  // guards form population so it runs exactly once per template selection
  // without this, navigating back to PaymentDetailsScreen would re-trigger
  // the populate effect and overwrite any user edits
  const markFormPopulated = useCallback(() => {
    setHasPopulatedForm(true);
  }, []);

  const value = useMemo<TemplateContextType>(
    () => ({
      isTemplateMode,
      template,
      loading: templateLoading,
      templateFields,
      excludedRows,
      toggleRowInclusion,
      isPaymentFieldEditable,
      allowExpedite,
      maxExpediteChoice,
      convertedPayments,
      templateQueryString,
      hasPopulatedForm,
      markFormPopulated,
      resetTemplateState,
    }),
    [
      isTemplateMode,
      template,
      templateLoading,
      excludedRows,
      toggleRowInclusion,
      isPaymentFieldEditable,
      allowExpedite,
      maxExpediteChoice,
      convertedPayments,
      templateQueryString,
      hasPopulatedForm,
      markFormPopulated,
      resetTemplateState,
    ]
  );

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
};

export default TemplateContext;
